/**
 * Group Structure tab for Customer Profile.
 * Shows the customer's group hierarchy and all companies in the group.
 * CR-4579, CR-4669
 */
import { useState, useEffect, useCallback } from 'react';
import { Plus, Loader2, ChevronRight, ChevronDown, Network, Lock, Star, FolderTree, MapPin } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { coreAPI, Company } from '../../lib/api';
import { CustomerProfile } from '../../lib/api/types/customers';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

interface GroupStructureTabProps {
  customer: CustomerProfile;
  isHelixAdmin: boolean;
  isSupplier: boolean;
}

interface AddChildForm {
  name: string;
  primary_email: string;
  country: string;
}

export default function GroupStructureTab({ customer, isHelixAdmin, isSupplier }: GroupStructureTabProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [groupCompanies, setGroupCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState<AddChildForm>({ name: '', primary_email: '', country: '' });
  const [saving, setSaving] = useState(false);

  const groupId = customer.company_details?.group;
  const groupName = customer.company_details?.group_name;
  const currentCompanyId = customer.company;

  const fetchGroupData = useCallback(async () => {
    setLoading(true);
    try {
      if (groupId) {
        // CR-4669: The default /core/companies/?group=X list is gated by
        // IsHelixUser — customer users calling it receive 403 and the tab
        // renders "0 companies / No companies in this group". Use the
        // dedicated my_group_members action instead, which any
        // authenticated user can call.
        // CR-4669 round 3: pass the customer's own group_id so admin /
        // supplier users browsing /customers/{id} see THIS customer's
        // group hierarchy, not the caller's own group (which was
        // returning 0 for admin users on the Skybridge profile).
        const companiesRes = await coreAPI.getMyGroupMembers({
          company_type: 'customer',
          group: groupId,
        });
        const companies = companiesRes.data.results || [];
        // CR-4669: Include ALL companies in the group (including current)
        // Sort so current company appears first
        companies.sort((a, b) => {
          if (a.id === currentCompanyId) return -1;
          if (b.id === currentCompanyId) return 1;
          return a.name.localeCompare(b.name);
        });
        setGroupCompanies(companies);
      } else {
        setGroupCompanies([]);
      }
    } catch (err) {
      console.error('Failed to fetch group structure:', err);
    } finally {
      setLoading(false);
    }
  }, [groupId, currentCompanyId]);

  useEffect(() => {
    fetchGroupData();
  }, [fetchGroupData]);

  const handleAddChild = async () => {
    if (!addForm.name.trim()) return;
    setSaving(true);
    try {
      await coreAPI.createCompany({
        name: addForm.name.trim(),
        company_type: 'customer',
        primary_email: addForm.primary_email,
        country: addForm.country,
        group: groupId || undefined,
        is_active: true,
      });
      setShowAddForm(false);
      setAddForm({ name: '', primary_email: '', country: '' });
      fetchGroupData();
    } catch (err) {
      console.error('Failed to create child company:', err);
    } finally {
      setSaving(false);
    }
  };

  // CR-4669: Check if user can access a company in the group.
  // Helix admins and suppliers can access any company. Customer users
  // can only access their own company.
  const canAccessCompany = (companyId: number): boolean => {
    if (isHelixAdmin || isSupplier) return true;
    return companyId === (user?.company ?? currentCompanyId);
  };

  const handleCompanyClick = (company: Company) => {
    if (canAccessCompany(company.id)) {
      navigate(`/customers/${company.id}`);
    } else {
      toast.info(
        `You do not have access to ${company.name}. Please contact your administrator if access is required.`,
      );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <span className="ml-2 text-muted-foreground">Loading group structure...</span>
      </div>
    );
  }

  // CR-4669: Only helix admin and supplier admin can add child companies
  const canAddChild = isHelixAdmin || isSupplier;

  // CR-4867 round 2: render the group + member companies as an indented
  // hierarchy tree (Admin Setup style), not a flat list. Group node is the
  // root; each company sits one level under it. Current company is pinned
  // first and visually highlighted with a Star + "Current" pill.
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Network className="h-5 w-5" />
              Group Structure
            </CardTitle>
            {canAddChild && groupId && (
              <Button size="sm" onClick={() => setShowAddForm(!showAddForm)}>
                <Plus className="h-4 w-4 mr-1" />
                Add Child Company
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {showAddForm && (
            <div className="border rounded-lg p-4 mb-4 bg-muted/20 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="text-sm font-medium mb-1 block">Company Name *</label>
                  <input
                    type="text"
                    value={addForm.name}
                    onChange={(e) => setAddForm({ ...addForm, name: e.target.value })}
                    placeholder="Enter company name"
                    className="w-full px-3 py-2 border rounded-md text-sm"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Email</label>
                  <input
                    type="email"
                    value={addForm.primary_email}
                    onChange={(e) => setAddForm({ ...addForm, primary_email: e.target.value })}
                    placeholder="contact@company.com"
                    className="w-full px-3 py-2 border rounded-md text-sm"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-1 block">Country</label>
                  <input
                    type="text"
                    value={addForm.country}
                    onChange={(e) => setAddForm({ ...addForm, country: e.target.value })}
                    placeholder="e.g. United Kingdom"
                    className="w-full px-3 py-2 border rounded-md text-sm"
                  />
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" size="sm" onClick={() => setShowAddForm(false)}>Cancel</Button>
                <Button size="sm" onClick={handleAddChild} disabled={saving || !addForm.name.trim()}>
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                  Create
                </Button>
              </div>
            </div>
          )}

          {!groupId ? (
            <p className="text-sm text-muted-foreground py-4">
              This customer does not belong to any group.
            </p>
          ) : (
            <div className="select-none">
              {/* Root: the group itself */}
              <div
                className="flex items-center gap-2 py-2 px-2 rounded-md hover:bg-muted/50 cursor-pointer transition-colors"
                onClick={() => navigate(`/customers/groups/${groupId}`)}
              >
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
                <FolderTree className="h-4 w-4 text-purple-500" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium truncate">{groupName || 'Customer Group'}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {groupCompanies.length} {groupCompanies.length === 1 ? 'company' : 'companies'}
                  </span>
                </div>
                <Badge variant="outline" className="text-xs capitalize border-purple-300 bg-purple-50 text-purple-700">
                  Group
                </Badge>
              </div>

              {/* Child companies, indented under the group with a left guide rail */}
              {groupCompanies.length > 0 ? (
                <div className="border-l border-border ml-4">
                  {groupCompanies.map((company) => {
                    const isCurrent = company.id === currentCompanyId;
                    const hasAccess = canAccessCompany(company.id);
                    const location = company.city ? `${company.city}, ${company.country || ''}` : (company.country || 'No location');
                    return (
                      <div
                        key={company.id}
                        className={`flex items-center gap-2 py-2 px-2 rounded-md transition-colors ${
                          isCurrent
                            ? 'bg-primary/5 border border-primary/30'
                            : hasAccess
                              ? 'hover:bg-muted/50 cursor-pointer'
                              : 'opacity-60'
                        }`}
                        style={{ paddingLeft: '28px' }}
                        onClick={() => !isCurrent && handleCompanyClick(company)}
                      >
                        <span className="w-5" />
                        <MapPin className={`h-4 w-4 ${isCurrent ? 'text-primary' : 'text-blue-500'}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium truncate text-sm">{company.name}</span>
                            {isCurrent && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-primary/10 text-primary text-xs font-semibold rounded-full">
                                <Star className="h-3 w-3" />
                                Current
                              </span>
                            )}
                            {!hasAccess && !isCurrent && (
                              <Lock className="h-3.5 w-3.5 text-muted-foreground" />
                            )}
                          </div>
                          <span className="text-xs text-muted-foreground">{location}</span>
                        </div>
                        <Badge variant="outline" className="text-xs capitalize border-blue-300 bg-blue-50 text-blue-700">
                          Company
                        </Badge>
                        {hasAccess && !isCurrent && <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground py-4 pl-8">
                  No companies in this group.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
